import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { DIALECTS, IMAGES } from "../../data/content";
import { Reveal } from "./Reveal";

const FlagshipCard = () => (
  <Reveal className="md:col-span-7" y={36}>
    <motion.article
      data-testid="dialect-card-levantine"
      whileHover="hover"
      className="group relative min-h-[420px] overflow-hidden rounded-3xl border border-gold/50 md:min-h-[460px]"
    >
      <motion.img
        src={IMAGES.calligraphy}
        alt="Arabic calligraphy on paper"
        variants={{ hover: { scale: 1.04 } }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-deep/95 via-deep/70 to-deep/25" />
      <div className="relative flex h-full flex-col justify-between p-8 md:p-10">
        <div className="flex items-start justify-between">
          <span className="rounded-full border border-gold/60 bg-gold/15 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-gold">
            {DIALECTS.flagship.tag}
          </span>
          <span className="font-arabic text-5xl text-paper/90 md:text-6xl">
            {DIALECTS.flagship.arabic}
          </span>
        </div>
        <div className="max-w-lg">
          <h3 className="font-serif text-3xl text-paper md:text-4xl">{DIALECTS.flagship.name}</h3>
          <p className="mt-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-paper/60">
            <MapPin className="h-3 w-3" /> {DIALECTS.flagship.region}
          </p>
          <p className="mt-4 text-sm leading-relaxed text-paper/80 md:text-base">
            {DIALECTS.flagship.description}
          </p>
        </div>
      </div>
    </motion.article>
  </Reveal>
);

const DialectCard = ({ d, wide }) => (
  <Reveal className={wide ? "md:col-span-7" : "md:col-span-5"} y={36}>
    <article
      data-testid={`dialect-card-${d.id}`}
      className="group flex h-full min-h-[280px] flex-col justify-between rounded-3xl border border-line bg-surface p-8 transition-colors duration-300 hover:border-jade md:p-9"
    >
      <div className="flex items-start justify-between gap-4">
        <span className="rounded-full border border-line bg-sand px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-ink-soft">
          {d.tag}
        </span>
        <span className="font-arabic text-4xl text-pine/25 transition-colors duration-300 group-hover:text-pine/50">
          {d.arabic}
        </span>
      </div>
      <div className="mt-10">
        <h3 className="font-serif text-2xl text-ink">{d.name}</h3>
        <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-jade">
          {d.region}
        </p>
        <p className="mt-3 text-sm leading-relaxed text-ink-soft">{d.description}</p>
      </div>
    </article>
  </Reveal>
);

export const Dialects = () => (
  <section id="dialects" className="py-24 md:py-32">
    <div className="mx-auto max-w-7xl px-6">
      <Reveal>
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="eyebrow">What we teach</p>
            <h2 className="mt-3 font-serif text-3xl tracking-tight text-ink sm:text-4xl md:text-5xl">
              Four paths into Arabic
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-ink-soft">
            Study the language of literature or a living street dialect — with Levantine as our
            main preferred dialect.
          </p>
        </div>
      </Reveal>

      <div className="mt-12 grid gap-5 md:grid-cols-12">
        <FlagshipCard />
        <DialectCard d={DIALECTS.others[0]} />
        <DialectCard d={DIALECTS.others[1]} wide={false} />
        <DialectCard d={DIALECTS.others[2]} wide />
      </div>
    </div>
  </section>
);

export default Dialects;
